import React, { useState, useEffect } from 'react';
import { topicApi } from '../services/api.ts';
import { authService } from '../services/auth.ts';
import NavigationMenu from './NavigationMenu.tsx';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

interface SeriesInfoEditPageProps {
  teamId: string;
  teamName: string;
  onBack: () => void;
  onNavigate: (page: string) => void;
  onShowSeriesInfoModal?: () => void;
}

const SeriesInfoEditPage: React.FC<SeriesInfoEditPageProps> = ({ 
  teamId, 
  teamName, 
  onBack, 
  onNavigate,
  onShowSeriesInfoModal 
}) => {
  
  const [markdown, setMarkdown] = useState('');
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Character limit constants
  const MAX_CHARACTERS = 51200; // 50KB
  const WARNING_THRESHOLD = 45000; // Show warning at 45KB

  // Simple sensitive data patterns (basic detection)
  const SENSITIVE_PATTERNS = [
    /password\s*[:=]\s*\S+/i,
    /api[_-]?key\s*[:=]\s*[A-Za-z0-9+/=]{20,}/i,
    /secret\s*[:=]\s*\S+/i,
    /token\s*[:=]\s*[A-Za-z0-9+/=]{20,}/i,
    /\b[A-Z0-9]{20,}\b/, // Generic long alphanumeric strings
  ];

  const detectSensitiveContent = (text: string): string[] => {
    const warnings: string[] = [];
    SENSITIVE_PATTERNS.forEach(pattern => {
      if (pattern.test(text)) {
        warnings.push('Potential sensitive data detected');
      }
    });
    return warnings;
  };

  const sensitiveWarnings = detectSensitiveContent(markdown);

  useEffect(() => {
    if (teamId) {
      loadSeriesInfo();
    }
  }, [teamId]);

  useEffect(() => {
    // Update preview when markdown changes
    if (markdown) {
      // Use proper markdown parsing like the backend
      const htmlPreview = DOMPurify.sanitize(marked(markdown));
      setPreview(htmlPreview);
    } else {
      setPreview('');
    }
  }, [markdown]);

  const loadSeriesInfo = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await topicApi.getSeriesInfoSource(teamId);
      // Use markdown directly from the source endpoint
      setMarkdown(response.markdown);
    } catch (err: any) {
      if (err.response?.status === 404) {
        // No custom content exists, load default template as markdown
        setMarkdown(getDefaultMarkdownTemplate());
      } else {
        setError('Failed to load series information');
        console.error('Error loading series info:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const saveSeriesInfo = async (redirectAfterSave = false) => {
    if (!teamId || !markdown.trim()) {
      setError('Content is required');
      return;
    }

    // Client-side length validation
    if (markdown.length > MAX_CHARACTERS) {
      setError(`Content exceeds maximum length of ${Math.floor(MAX_CHARACTERS / 1024)}KB`);
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await topicApi.saveSeriesInfo(teamId, markdown);

      setSuccess('Series information saved successfully!');
      
      if (redirectAfterSave) {
        // Go back to main page and show modal
        setTimeout(() => {
          onBack();
          if (onShowSeriesInfoModal) {
            onShowSeriesInfoModal();
          }
        }, 1000);
      }
    } catch (err: any) {
      // Handle specific error messages from backend
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to save series information');
      }
      console.error('Error saving series info:', err);
    } finally {
      setSaving(false);
    }
  };

  const getDefaultMarkdownTemplate = (): string => {
    return `## Welcome to Your Knowledge Sharing Series!

**Series organizer:** [your-alias](https://phonetool.amazon.com/users/your-alias)

This series is designed to help your team:

* **Share expertise** - Quick 10-15 minute presentations on services, solutions, and best practices
* **Reduce research time** - Help colleagues answer customer questions faster
* **Scale knowledge** - Turn individual learning into team-wide expertise
* **Track impact** - See how your topics boost confidence and save time

### Session Format

Informal 10-15 minute sessions with no formal expectations. Share:

* Service demonstrations and explanations
* Solutions to customer problems
* Best practices and lessons learned
* New features and capabilities

### Benefits

**Primary:** Customers get answers quicker and opportunities accelerate faster

**Secondary:** Knowledge retention, skill development, and team collaboration`;
  };

  const handleNavigation = (page: string) => {
    if (page === 'main') {
      onBack();
    } else {
      onNavigate(page);
    }
  };

  return (
    <div className="app">
      <header className="app-header">
        <div>
          <h1 style={{ margin: 0 }}>ShareBrief</h1>
          <h2 style={{ 
            margin: '8px 0 0 0', 
            color: '#555', 
            fontWeight: '600',
            fontSize: '1.5rem'
          }}>
            Edit Series Information
          </h2>
          {teamName && (
            <h2 style={{ 
              margin: '4px 0 0 0', 
              color: '#2563eb', 
              fontWeight: '600',
              fontSize: '1.5rem'
            }}>
              {teamName}
            </h2>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          {(() => {
            const userInfo = authService.getUserInfo();
            return userInfo ? (
              <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                Welcome <span style={{ color: '#2563eb', fontWeight: '600' }}>{userInfo.name}</span>
              </div>
            ) : null;
          })()}
        </div>
      </header>

      <NavigationMenu 
        currentPage="series-info-edit" 
        currentTeamId={teamId}
        onNavigate={handleNavigation}
      />

      <div className="main-content" style={{ padding: '20px' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            Loading series information...
          </div>
        )}

        {error && (
          <div style={{ 
            color: '#dc2626', 
            backgroundColor: '#fef2f2', 
            padding: '12px', 
            borderRadius: '4px',
            marginBottom: '16px'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ 
            color: '#059669', 
            backgroundColor: '#f0fdf4', 
            padding: '12px', 
            borderRadius: '4px',
            marginBottom: '16px'
          }}>
            {success}
          </div>
        )}

        {!loading && (
          <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)' }}>
            {/* Informational Message */}
            {teamName && (
              <div style={{
                backgroundColor: '#f0f9ff',
                border: '1px solid #bfdbfe',
                borderRadius: '6px',
                padding: '12px 16px',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#1e40af'
              }}>
                You are currently editing the series information for <strong>{teamName}</strong>. 
                This content helps colleagues understand your knowledge sharing series and encourages participation.
              </div>
            )}
            
            <div style={{ 
              display: 'flex', 
              gap: '20px', 
              flex: 1,
              marginBottom: '20px'
            }}>
              {/* Markdown Editor */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <h3 style={{ marginTop: 0, marginBottom: '8px' }}>Markdown Editor</h3>
                <textarea
                  value={markdown}
                  onChange={(e) => setMarkdown(e.target.value)}
                  placeholder="Enter your series information in markdown format..."
                  style={{
                    flex: 1,
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                    resize: 'none',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                
                {/* Character Counter */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginTop: '8px',
                  fontSize: '12px'
                }}>
                  <div style={{ 
                    color: markdown.length > MAX_CHARACTERS ? '#dc2626' : 
                           markdown.length > WARNING_THRESHOLD ? '#f59e0b' : '#6b7280',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    {markdown.length > MAX_CHARACTERS && <span style={{ color: '#dc2626' }}>⚠️</span>}
                    {markdown.length.toLocaleString()} / {MAX_CHARACTERS.toLocaleString()} characters
                    {markdown.length > MAX_CHARACTERS && (
                      <span style={{ color: '#dc2626', fontWeight: 'bold' }}>
                        (Exceeds limit by {(markdown.length - MAX_CHARACTERS).toLocaleString()})
                      </span>
                    )}
                  </div>
                  <div style={{ color: '#6b7280' }}>
                    {Math.floor(markdown.length / 1024)}KB / 50KB
                  </div>
                </div>

                {/* Sensitive Content Warning */}
                {sensitiveWarnings.length > 0 && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    backgroundColor: '#fef3c7',
                    border: '1px solid #f59e0b',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#92400e',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span>⚠️</span>
                    <span>Warning: This content may contain sensitive information (passwords, API keys, etc.). Please review before saving.</span>
                  </div>
                )}
              </div>

              {/* Live Preview */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <h3 style={{ marginTop: 0, marginBottom: '8px' }}>Live Preview</h3>
                <div
                  style={{
                    flex: 1,
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: '#f9fafb',
                    overflow: 'auto',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    boxSizing: 'border-box'
                  }}
                  dangerouslySetInnerHTML={{ __html: preview || '<em>Preview will appear here...</em>' }}
                />
              </div>
            </div>

            {/* Action Buttons - Now properly below text boxes */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '20px',
              paddingBottom: '20px',
              borderTop: '1px solid #e0e0e0',
              marginTop: 'auto'
            }}>
              <button
                onClick={onBack}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => saveSeriesInfo(false)}
                  disabled={saving || markdown.length > MAX_CHARACTERS}
                  style={{
                    backgroundColor: markdown.length > MAX_CHARACTERS ? '#9ca3af' : '#2563eb',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: (saving || markdown.length > MAX_CHARACTERS) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: (saving || markdown.length > MAX_CHARACTERS) ? 0.6 : 1
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>

                <button
                  onClick={() => saveSeriesInfo(true)}
                  disabled={saving || markdown.length > MAX_CHARACTERS}
                  style={{
                    backgroundColor: markdown.length > MAX_CHARACTERS ? '#9ca3af' : '#059669',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: (saving || markdown.length > MAX_CHARACTERS) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: (saving || markdown.length > MAX_CHARACTERS) ? 0.6 : 1
                  }}
                >
                  {saving ? 'Publishing...' : 'Publish'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SeriesInfoEditPage;
